import Stage = createjs.Stage;

import { getLogger } from "@goodgamestudios/logger";
import { ClientFunnelGameStates } from "./ClientFunnelGameStates";
import { ClientFunnelPlayTimeStateVO } from "./ClientFunnelPlayTimeStateVO";

const logger = getLogger("Tracking.ClientFunnelPlayTimeController");

// _playtimeStates represent the time for the FI-events execution.
// timeDelay-part of the FirstInstancePlaytimeStateVO is needed to calculate the next timer-delay automatically
const _playtimeStates: ClientFunnelPlayTimeStateVO[] = [
    new ClientFunnelPlayTimeStateVO(0.5, ClientFunnelGameStates.PLAYTIME_0_5_MINUTES),
    new ClientFunnelPlayTimeStateVO(1, ClientFunnelGameStates.PLAYTIME_X_MINUTES_1),
    new ClientFunnelPlayTimeStateVO(2, ClientFunnelGameStates.PLAYTIME_X_MINUTES_2),
    new ClientFunnelPlayTimeStateVO(5, ClientFunnelGameStates.PLAYTIME_X_MINUTES_5),
    new ClientFunnelPlayTimeStateVO(10, ClientFunnelGameStates.PLAYTIME_X_MINUTES_10),
    new ClientFunnelPlayTimeStateVO(15, ClientFunnelGameStates.PLAYTIME_X_MINUTES_15),
    new ClientFunnelPlayTimeStateVO(30, ClientFunnelGameStates.PLAYTIME_X_MINUTES_30),
    new ClientFunnelPlayTimeStateVO(45, ClientFunnelGameStates.PLAYTIME_X_MINUTES_45),
    new ClientFunnelPlayTimeStateVO(60, ClientFunnelGameStates.PLAYTIME_X_MINUTES_60)
];

export class ClientFunnelPlayTimeController {

    private _timeoutId: number = undefined;
    private _currentPlayTimeIndex: number = 0;

    private _startTime: number = 0;
    private _elapsedTime: number = 0;
    private _pauseTime: number = 0;
    private _resumeTime: number = 0;

    private _hiddenFlag: string;
    private _visibilityChangeEvent: string;

    constructor(private _stage: Stage, private _trackStateHandler: Function) {
        if (!this._stage) {
            throw new Error("[ClientFunnelPlayTimeController] Stage must be defined");
        }
    }

    public start() {
        if (this._timeoutId) {
            return;
        }

        this._startTime = Date.now();
        this._elapsedTime = 0;
        this._currentPlayTimeIndex = 0;
        this._timeoutId = window.setTimeout(this.timerHandler, this.currentDelay);

        this.monitorVisibility();
    }

    private monitorVisibility(): void {
        if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support
            this._hiddenFlag = "hidden";
            this._visibilityChangeEvent = "visibilitychange";
        } else if (typeof (<any>document).msHidden !== "undefined") {
            this._hiddenFlag = "msHidden";
            this._visibilityChangeEvent = "msvisibilitychange";
        } else if (typeof (<any>document).webkitHidden !== "undefined") {
            this._hiddenFlag = "webkitHidden";
            this._visibilityChangeEvent = "webkitvisibilitychange";
        }

        document.addEventListener(this._visibilityChangeEvent, this.visibilityHandler, false);
    }

    private timerHandler = (): void => {
        if (this._currentPlayTimeIndex < _playtimeStates.length) {
            this._elapsedTime = _playtimeStates[this._currentPlayTimeIndex].timeDelayMilliseconds;
            this._trackStateHandler(_playtimeStates[this._currentPlayTimeIndex].eventState);
        }

        this._currentPlayTimeIndex++;

        if (this._currentPlayTimeIndex >= _playtimeStates.length) {
            this.dispose();
            return;
        }

        this._startTime = Date.now();
        this._timeoutId = window.setTimeout(this.timerHandler, this.currentDelay);
    }

    private visibilityHandler = (): void => {
        if (document[this._hiddenFlag]) {
            this.pausePlaytimeTracking();
        } else {
            this.resumePlaytimeTracking();
        }
    }

    private pausePlaytimeTracking(): void {
        this._pauseTime = Date.now();
        window.clearTimeout(this._timeoutId);
        this._timeoutId = undefined;
    }

    private resumePlaytimeTracking(): void {
        this._resumeTime = Date.now();
        this._timeoutId = window.setTimeout(this.timerHandler, this.currentDelay - (this._pauseTime - this._startTime));
        this._startTime += this._resumeTime - this._pauseTime;
    }

    public dispose(): void {
        if (this._timeoutId) {
            window.clearTimeout(this._timeoutId);
            this._timeoutId = undefined;
        }

        document.removeEventListener(this._visibilityChangeEvent, this.visibilityHandler);
    }

    /**
     * Current delay for the timer will be calculated as a difference between the next defined delay and the last delay.
     */
    private get currentDelay(): number {
        let delay: number = 0;

        if (this._currentPlayTimeIndex < _playtimeStates.length) {
            delay = _playtimeStates[this._currentPlayTimeIndex].timeDelayMilliseconds - this._elapsedTime;
        }

        return delay;
    }
}
